import * as React from "react";
import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Panel,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";
import { DomainRoutingTab, DomainSettingsTab, DomainSpamTab } from "./DomainTabs";
import { Plus } from "lucide-react";
import { useCreate } from "../../useCreate";

// One mail domain, and everything nested under it. Every shape here was read from a live node,
// never inferred from the Go structs.

interface Mailbox {
  address: string;
  localpart: string;
  domain: string;
  quota_mb: number;
  status: string;
  password?: string; // returned ONCE, on create/reset
}
interface Alias {
  source: string;
  localpart: string;
  catch_all: boolean;
  destinations: string[] | null;
  active: boolean;
}
interface Dkim {
  has_key: boolean;
  published: boolean;
  record_name: string;
  record_type: string;
  record_value: string;
}

function useDomain() {
  const { domain = "" } = useParams();
  return decodeURIComponent(domain);
}

// A password the server will never show again. It is displayed until dismissed rather than in
// a toast: a toast disappears on a timer, and this is the only moment the value exists.
function OneTimeSecret({ label, value, onDismiss }: { label: string; value: string; onDismiss: () => void }) {
  return (
    <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3">
      <div className="text-[12.5px] font-medium">{label}</div>
      <div className="mt-1 break-all font-mono text-[12.5px] text-fg">{value}</div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => void navigator.clipboard?.writeText(value)}>
          Copy
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
        <span className="text-[11.5px] text-muted">Shown once — the server keeps only a hash.</span>
      </div>
    </Panel>
  );
}

function MailboxesTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [secret, setSecret] = React.useState<{ label: string; value: string } | null>(null);
  const [confirmDel, setConfirmDel] = React.useState<Mailbox | null>(null);
  const base = `/mail/domains/${encodeURIComponent(domain)}/mailboxes`;

  const list = useQuery({
    queryKey: ["mail-mailboxes", domain],
    queryFn: () => client.apiFetch<{ items: Mailbox[] | null; total: number }>(base),
  });

  const create = useCreate({
    title: `Add a mailbox on ${domain}`,
    description:
      "A password is generated when you leave it blank, and shown once. The quota is the mailbox's own limit; 0 follows the account's plan.",
    submitLabel: "Create mailbox",
    fields: [
      { key: "localpart", label: "Local part", required: true, placeholder: "alice" },
      { key: "quota_mb", label: "Quota (MB)", placeholder: "0 = follow the plan" },
      { key: "password", label: "Password", type: "password", placeholder: "leave blank to generate one" },
    ],
    run: (v) =>
      client
        .apiFetch<Mailbox>(base, {
          method: "POST",
          body: JSON.stringify({
            localpart: v.localpart,
            quota_mb: Number(v.quota_mb || 0),
            password: v.password || undefined,
          }),
        })
        .then((m) => {
          if (m?.password) setSecret({ label: `Password for ${m.address}`, value: m.password });
          return m;
        }),
    invalidates: [["mail-mailboxes", domain], ["mail-domains"]],
    success: (v) => `${v.localpart}@${domain} created`,
  });

  const resetPw = useMutation({
    mutationFn: (m: Mailbox) =>
      client.apiFetch<Mailbox>(`${base}/${encodeURIComponent(m.localpart)}/password`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: (r, m) => {
      if (r?.password) setSecret({ label: `New password for ${m.address}`, value: r.password });
      else notify({ kind: "success", title: "Password reset" });
    },
    onError: (e) => notify({ kind: "error", title: "Reset failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const del = useMutation({
    mutationFn: (m: Mailbox) =>
      client.apiFetch<unknown>(`${base}/${encodeURIComponent(m.localpart)}`, { method: "DELETE" }),
    onSuccess: (_r, m) => {
      notify({ kind: "success", title: `${m.address} deleted` });
      void qc.invalidateQueries({ queryKey: ["mail-mailboxes", domain] });
      void qc.invalidateQueries({ queryKey: ["mail-domains"] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  // Rules and out-of-office belong to the person using the mailbox, but a domain admin has to be
  // able to see and remove them — "why is my mail disappearing" is almost always a filter somebody
  // set and forgot, and the admin console could not look.
  const [openBox, setOpenBox] = React.useState<string | null>(null);

  const columns: DataListColumn<Mailbox>[] = [
    { key: "address", label: "Address", sortable: true },
    {
      key: "quota_mb",
      label: "Quota",
      sortable: true,
      value: (m) => m.quota_mb ?? 0,
      // 0 is not "no quota" — it means the mailbox inherits, and showing a bare 0 has been read
      // as unlimited before. Say which rule is actually in force.
      render: (m) => (m.quota_mb > 0 ? `${m.quota_mb} MB` : <span className="text-muted">follows the plan</span>),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (m) => <StatusPill tone={m.status === "active" ? "ok" : "warn"}>{m.status}</StatusPill>,
    },
    {
      key: "actions",
      label: "",
      render: (m) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => setOpenBox(openBox === m.address ? null : m.address)}>
            {openBox === m.address ? "Close" : "Rules"}
          </Button>
          <Button size="sm" variant="ghost" disabled={resetPw.isPending} onClick={() => resetPw.mutate(m)}>
            Reset password
          </Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmDel(m)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Mailboxes"
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Add mailbox
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        {secret && <OneTimeSecret {...secret} onDismiss={() => setSecret(null)} />}
        <DataList
          items={list.data?.items ?? []}
          columns={columns}
          getRowKey={(m) => m.address}
          loading={list.isLoading}
          error={list.error}
          errorContext="mailboxes"
          emptyMessage="No mailboxes on this domain."
          expandedKey={openBox}
          renderExpanded={(m) => <MailboxRules domain={domain} localpart={m.localpart} />}
        />
      </div>
      {create.dialog}
      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={`Delete ${confirmDel?.address}?`}
        destructive
        confirmLabel="Delete mailbox"
        pending={del.isPending}
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel);
          setConfirmDel(null);
        }}
        body={
          <p>
            The mailbox stops receiving and its owner can no longer sign in. Stored mail is removed with it —
            take a backup first if any of it matters.
          </p>
        }
      />
    </Section>
  );
}

function AliasesTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = React.useState<Alias | null>(null);
  const base = `/mail/domains/${encodeURIComponent(domain)}/aliases`;

  const list = useQuery({
    queryKey: ["mail-aliases", domain],
    queryFn: () => client.apiFetch<{ items: Alias[] | null; total: number }>(base),
  });

  const create = useCreate({
    title: `Add an alias on ${domain}`,
    description:
      "Mail to the source address is delivered to every destination. Use @ as the local part for a catch-all. Destinations may be local mailboxes; an external address is delivered only when the account has external forwarding enabled (Account → Mail) and a default mailbox set — otherwise it is kept for the owner mailbox.",
    submitLabel: "Create alias",
    fields: [
      // The create field is `source`, NOT `localpart`. The response object carries `localpart`
      // and the request does not, and sending the wrong one is not a validation error — the
      // server sees an empty source and silently creates the DOMAIN CATCH-ALL. Measured on
      // node10: posting {localpart:"v2probe"} produced {source:"@oztag.click", catch_all:true},
      // i.e. a typo'd field name turned into "accept all mail for this domain".
      { key: "source", label: "Local part", required: true, placeholder: "sales  (or @ for catch-all)" },
      { key: "destination", label: "Destinations", required: true, placeholder: "alice@example.com, bob@other.net" },
    ],
    run: (v) =>
      client.apiFetch<Alias>(base, {
        method: "POST",
        body: JSON.stringify({ source: v.source, destination: v.destination }),
      }),
    invalidates: [["mail-aliases", domain]],
    success: (v) => `${v.source}@${domain} created`,
  });

  const del = useMutation({
    // The localpart is NOT percent-encoded. The server does not decode this path segment:
    // DELETE .../aliases/%40 answers "alias not found: %40" while .../aliases/@ deletes the
    // catch-all. encodeURIComponent here would make a catch-all undeletable from the UI, and
    // the failure reads as "it is already gone" rather than "the path was wrong".
    mutationFn: (a: Alias) => client.apiFetch<unknown>(`${base}/${a.localpart}`, { method: "DELETE" }),
    onSuccess: (_r, a) => {
      notify({ kind: "success", title: `${a.source} deleted` });
      void qc.invalidateQueries({ queryKey: ["mail-aliases", domain] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<Alias>[] = [
    {
      key: "source",
      label: "Address",
      sortable: true,
      render: (a) => (
        <span>
          {a.source}
          {a.catch_all && (
            <span className="ml-2 inline-block align-middle">
              <StatusPill tone="warn">catch-all</StatusPill>
            </span>
          )}
        </span>
      ),
    },
    {
      key: "destinations",
      label: "Delivers to",
      value: (a) => (a.destinations ?? []).join(", "),
      render: (a) => <span className="font-mono text-[12px]">{(a.destinations ?? []).join(", ")}</span>,
    },
    {
      key: "active",
      label: "State",
      sortable: true,
      value: (a) => (a.active ? "active" : "off"),
      render: (a) => <StatusPill tone={a.active ? "ok" : "warn"}>{a.active ? "active" : "off"}</StatusPill>,
    },
    {
      key: "actions",
      label: "",
      render: (a) => (
        <div className="flex justify-end">
          <Button size="sm" variant="danger" onClick={() => setConfirmDel(a)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Aliases"
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Add alias
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        <DataList
          items={list.data?.items ?? []}
          columns={columns}
          getRowKey={(a) => a.source}
          loading={list.isLoading}
          error={list.error}
          errorContext="aliases"
          emptyMessage="No aliases on this domain."
          emptyHint="An alias forwards one address to one or more destinations; a catch-all takes everything not otherwise matched."
        />
      </div>
      {create.dialog}
      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={`Delete ${confirmDel?.source}?`}
        destructive
        confirmLabel="Delete alias"
        pending={del.isPending}
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel);
          setConfirmDel(null);
        }}
        body={<p>Mail sent to this address will be rejected as an unknown recipient from the next lookup.</p>}
      />
    </Section>
  );
}

function DkimTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirmRotate, setConfirmRotate] = React.useState(false);
  const base = `/mail/domains/${encodeURIComponent(domain)}/dkim`;

  const dkim = useQuery({
    queryKey: ["mail-dkim", domain],
    queryFn: () => client.apiFetch<Dkim>(base),
  });

  const rotate = useMutation({
    mutationFn: () => client.apiFetch<unknown>(`${base}/rotate`, { method: "POST" }),
    onSuccess: () => {
      notify({
        kind: "success",
        title: "Signing key rotated",
        body: "The new public key must be published in DNS before the old one is retired.",
      });
      void qc.invalidateQueries({ queryKey: ["mail-dkim", domain] });
    },
    onError: (e) => notify({ kind: "error", title: "Rotate failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const d = dkim.data;
  return (
    <Section
      title="DKIM signing"
      hint={d ? (d.has_key ? (d.published ? "signing and published" : "key present, NOT published") : "no key") : ""}
      actions={
        <Button size="sm" variant="ghost" disabled={rotate.isPending} onClick={() => setConfirmRotate(true)}>
          {rotate.isPending ? "Rotating…" : "Rotate key"}
        </Button>
      }
    >
      <StateView loading={dkim.isLoading} error={dkim.error} errorContext="the DKIM record">
        {d && (
          <>
            {/* Two facts, not one. A key that exists but is not published in DNS signs mail that
                every receiver then fails to verify — which is worse than not signing at all, and
                a single "DKIM: on" badge hides exactly that case. */}
            <div className="mb-3 flex flex-wrap gap-2">
              <StatusPill tone={d.has_key ? "ok" : "bad"}>
                {d.has_key ? "signing key present" : "no signing key — outbound mail is unsigned"}
              </StatusPill>
              <StatusPill tone={d.published ? "ok" : "bad"}>
                {d.published ? "published in DNS" : "NOT published — receivers cannot verify the signature"}
              </StatusPill>
            </div>
            {d.record_name && (
              <div className="overflow-hidden rounded-lg border border-line">
                <div className="border-b border-line px-3 py-2">
                  <div className="text-[11.5px] uppercase tracking-wide text-muted">Record</div>
                  <div className="font-mono text-[12px]">
                    {d.record_name} <span className="text-muted">{d.record_type}</span>
                  </div>
                </div>
                <div className="px-3 py-2">
                  <div className="text-[11.5px] uppercase tracking-wide text-muted">Value</div>
                  <div className="mt-1 max-h-40 overflow-auto break-all font-mono text-[11.5px] text-muted">
                    {d.record_value}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="mt-2"
                    onClick={() => void navigator.clipboard?.writeText(d.record_value)}
                  >
                    Copy value
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </StateView>
      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setConfirmRotate}
        title="Rotate the DKIM signing key?"
        destructive
        confirmLabel="Rotate key"
        pending={rotate.isPending}
        onConfirm={() => {
          setConfirmRotate(false);
          rotate.mutate();
        }}
        body={
          <p>
            A new key is generated and mail is signed with it immediately. Until the new public key is published in
            DNS and has propagated, receivers cannot verify the signature — mail in flight signed with the old key
            stays verifiable only while the old record remains.
          </p>
        }
      />
    </Section>
  );
}

export function MailDomainDetailScreen() {
  const domain = useDomain();
  return (
    <div>
      <PageHeader
        title={domain}
        crumbs={[
          { label: "Mail domains", to: "/mail/domains" },
          { label: domain },
        ]}
      />
      <Tabs defaultValue="mailboxes">
        <TabsList>
          <TabsTrigger value="mailboxes">Mailboxes</TabsTrigger>
          <TabsTrigger value="aliases">Aliases</TabsTrigger>
          <TabsTrigger value="dkim">DKIM</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="spam">Spam</TabsTrigger>
          <TabsTrigger value="routing">Routing</TabsTrigger>
        </TabsList>
        <TabsContent value="mailboxes">
          <MailboxesTab domain={domain} />
        </TabsContent>
        <TabsContent value="aliases">
          <AliasesTab domain={domain} />
        </TabsContent>
        <TabsContent value="dkim">
          <DkimTab domain={domain} />
        </TabsContent>
        <TabsContent value="settings">
          <DomainSettingsTab domain={domain} />
        </TabsContent>
        <TabsContent value="spam">
          <DomainSpamTab domain={domain} />
        </TabsContent>
        <TabsContent value="routing">
          <DomainRoutingTab domain={domain} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// One mailbox's filters and out-of-office, for a domain admin.
//
// Both routes are domain_admin and neither was reachable from the admin console — so "my mail is
// disappearing" could not be answered here, though the answer is almost always a rule somebody
// set and forgot.
function MailboxRules({ domain, localpart }: { domain: string; localpart: string }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const base = `/mail/domains/${encodeURIComponent(domain)}/mailboxes/${encodeURIComponent(localpart)}`;
  const KEY = ["mbx-rules", domain, localpart];

  const filters = useQuery({
    queryKey: KEY,
    queryFn: () => client.apiFetch<{ items: { id: number | string; name?: string; field?: string; match_type?: string; value?: string; action?: string; action_arg?: string }[] | null }>(`${base}/filters`),
    retry: false,
  });
  const auto = useQuery({
    queryKey: ["mbx-auto", domain, localpart],
    queryFn: () => client.apiFetch<{ enabled?: boolean; subject?: string; message?: string; start_date?: string; end_date?: string }>(`${base}/autoresponder`),
    retry: false,
  });

  // The id is the rule's POSITION in the shared script; the name lets the server refuse (409) when a
  // Roundcube edit moved another rule into that place since this list was read.
  const remove = (id: string, name: string) =>
    client
      .apiFetch(`${base}/filters/${encodeURIComponent(id)}?name=${encodeURIComponent(name)}`, { method: "DELETE" })
      .then(() => {
        notify({ kind: "success", title: "Rule removed" });
        void qc.invalidateQueries({ queryKey: KEY });
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Could not remove it", body: e instanceof Error ? e.message : String(e) }));

  const items = filters.data?.items ?? [];

  return (
    <div className="grid gap-4 text-[12.5px] lg:grid-cols-2">
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Rules ({items.length})</div>
        <StateView loading={filters.isLoading} error={filters.error} errorContext="this mailbox's rules">
          {items.length === 0 ? (
            <div className="text-muted">No rules — mail goes to the Inbox unless the spam filter moves it.</div>
          ) : (
            <ul className="space-y-1">
              {items.map((f) => (
                <li key={String(f.id)} className="flex items-start justify-between gap-3 border-b border-line py-1">
                  <span>
                    if <span className="text-fg">{f.field}</span> {f.match_type}{" "}
                    <span className="font-mono">{f.value}</span> → {f.action}
                    {f.action_arg ? ` ${f.action_arg}` : ""}
                  </span>
                  <Button size="sm" variant="danger" onClick={() => void remove(String(f.id), f.name ?? "")}>Remove</Button>
                </li>
              ))}
            </ul>
          )}
        </StateView>
      </div>
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Out of office</div>
        <StateView loading={auto.isLoading} error={auto.error} errorContext="the automatic reply">
          {auto.data?.enabled ? (
            <div>
              <StatusPill tone="accent">on</StatusPill>
              <div className="mt-1">{auto.data.subject || <span className="text-muted">(no subject)</span>}</div>
              {(auto.data.start_date || auto.data.end_date) && (
                <div className="mt-0.5 text-[11.5px] text-muted">
                  {auto.data.start_date || "—"} to {auto.data.end_date || "—"}
                </div>
              )}
              <Button size="sm" variant="ghost" className="mt-2"
                onClick={() =>
                  void client
                    .apiFetch(`${base}/autoresponder`, { method: "PUT", body: JSON.stringify({ enabled: false }) })
                    .then(() => {
                      notify({ kind: "success", title: "Automatic reply turned off" });
                      void qc.invalidateQueries({ queryKey: ["mbx-auto", domain, localpart] });
                    })
                    .catch((e: unknown) => notify({ kind: "error", title: "Could not turn it off", body: e instanceof Error ? e.message : String(e) }))
                }>
                Turn it off
              </Button>
            </div>
          ) : (
            <div className="text-muted">No automatic reply set.</div>
          )}
        </StateView>
      </div>
    </div>
  );
}
